<template>
  <div>
    <div
      class="taint-sink"
      v-if="sinkInfo?.info?.length"
    >
      <h3>{{ $t('vulnerabilityInformation.taintSink') }}</h3>
      <el-descriptions
        :column="1"
        border
        v-if="isMobile"
      >
        <el-descriptions-item
          v-for="item in sinkInfo?.info"
          :key="item.item"
          :label="item.item"
        >
          {{ item.information }}
        </el-descriptions-item>
      </el-descriptions>
      <el-table
        v-else
        :data="sinkInfo?.info"
        style="width: 100%"
        border
        :span-method="objectSpanMethod"
      >
        <el-table-column
          prop="taint"
          :label="$t('vulnerabilityInformation.taint')"
          width="150"
          align="center"
        ></el-table-column>
        <el-table-column
          prop="item"
          :label="$t('vulnerabilityInformation.item')"
          width="150"
          align="center"
        ></el-table-column>
        <el-table-column
          prop="information"
          :label="$t('vulnerabilityInformation.information')"
          align="center"
        ></el-table-column>
      </el-table>
      <div
        class="codeWrapper"
        v-if="sinkInfo?.sinkCode"
      >
        <div class="codeTitle">
          <h3>{{ localLanguage }}</h3>
          <img
            src="@assets/codeScan/code_scan_fff.png"
            @click="copyOxpeckerIp(sinkInfo?.sinkCode)"
          />
        </div>
        <CodeMirror
          ref="sinkCode"
          :value="sinkInfo?.sinkCode"
          :options="{
              mode: 'text/x-java',
              theme: 'base16-dark',
              lineNumbers: false,
              line: true,
              readOnly: true,
            }"
        />
      </div>
    </div>

    <div class="taint-sink">
      <h3>{{ $t('vulnerabilityInformation.taintSource') }}</h3>
      <!-- Source info table/description similar to sink -->
      <el-descriptions
        :column="1"
        border
        v-if="isMobile"
      >
        <el-descriptions-item
          v-for="item in sourceInfo?.info"
          :key="item.item"
          :label="item.item"
        >
          {{ item.information }}
        </el-descriptions-item>
      </el-descriptions>
      <el-table
        v-else
        :data="sourceInfo?.info"
        style="width: 100%"
        border
        :span-method="objectSpanMethod"
      >
        <el-table-column
          prop="taint"
          :label="$t('vulnerabilityInformation.taint')"
          width="150"
          align="center"
        ></el-table-column>
        <el-table-column
          prop="item"
          :label="$t('vulnerabilityInformation.item')"
          width="150"
          align="center"
        ></el-table-column>
        <el-table-column
          prop="information"
          :label="$t('vulnerabilityInformation.information')"
          align="center"
        ></el-table-column>
      </el-table>

      <div
        class="codeWrapper"
        v-if="sourceInfo?.sourceCode"
      >
        <div class="codeTitle">
          <h3>{{ localLanguage }}</h3>
          <img
            src="@assets/codeScan/code_scan_fff.png"
            @click="copyOxpeckerIp(sourceInfo?.sourceCode)"
          />
        </div>
        <CodeMirror
          :value="sourceInfo?.sourceCode"
          :options="{
              mode: 'text/x-java',
              theme: 'base16-dark',
              lineNumbers: false,
              line: true,
              readOnly: true,
            }"
        />
      </div>
    </div>
    <div class="moduleWrapper">
      <!-- POC Script section -->
      <div
        class="codeWrapper"
        v-if="vul.pocScript"
      >
        <h3 style="margin-bottom: 8px">{{ $t('vulnerabilityInformation.proofOfConcept') }}</h3>
        <div class="codeTitle">
          <h3>Python</h3>
          <img
            src="@assets/codeScan/code_scan_fff.png"
            @click="copyOxpeckerIp(vul.pocScript)"
          />
        </div>
        <CodeMirror
          :value="vul.pocScript"
          :options="{
                mode: 'text/x-python',
                theme: 'base16-dark',
                lineNumbers: true,
                line: true,
                readOnly: true,
              }"
        />
      </div>
      <!-- POC Output section -->
      <div
        class="codeWrapper"
        v-if="vul.pocScriptOutput"
      >
        <h3 style="margin-bottom: 8px">{{ $t('vulnerabilityInformation.proofExecutionResult') }}</h3>
        <CodeMirror
          :value="vul.pocScriptOutput"
          :options="{
                mode: 'text',
                theme: 'base16-dark',
                lineNumbers: true,
                line: true,
                readOnly: true,
              }"
        />
        <div
          class="images"
          v-if="images && images.length"
        >
          <div
            class="image-item"
            v-for="image in images"
            :key="image"
          >
            <el-image
              :src="image"
              alt=""
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { codemirror } from 'vue-codemirror';
import 'codemirror/lib/codemirror.css';

import { copyText } from '@/utils/copy-text';
import { LANGUAGE_TYPES } from '@/enum';

require('codemirror/mode/python/python');
require('codemirror/mode/clike/clike');
require('codemirror/theme/base16-dark.css');

/**
 * 从代码片段中提取起始行号
 * @param {string} codeSnippet 带行号的代码片段
 * @returns {number|null} 起始行号或null
 */
function extractStartLine(codeSnippet) {
  // 获取代码片段的第一行
  const firstLine = codeSnippet.split('\n')[0];

  // 使用正则表达式匹配行号
  // 格式可能是类似于 "3: module.exports" 或 "#L3-L28\n3: module"
  const match = firstLine.match(/#L(\d+)/);

  if (match) {
    // 优先使用第二个匹配组（实际行号）
    return parseInt(match[1], 10);
  }

  return null;
}

/**
 * 高亮指定行
 * @param {CodeMirror} cm CodeMirror实例
 * @param {string} codeSnippet 代码片段
 * @param {number} targetLine 目标行号（相对于原文件）
 */
function highlightLine(cm, codeSnippet, targetLine) {
  const startLine = extractStartLine(codeSnippet);

  // 清除 CodeMirror 中的所有行高亮
  const totalLines = cm.lineCount();
  for (let i = 0; i < totalLines; i++) {
    cm.removeLineClass(i, 'background', 'highlighted-line');
  }

  if (startLine === null) {
    console.error('无法提取起始行号');
    return;
  }

  const cmLineIndex = targetLine - startLine + 1;

  // 高亮行
  setTimeout(() => {
    cm.addLineClass(cmLineIndex, 'background', 'highlighted-line');
  });
}

export default {
  name: 'SinkVul',
  components: {
    CodeMirror: codemirror,
  },
  props: {
    language: {
      type: String,
      default: LANGUAGE_TYPES.JAVA,
    },
    sinkInfo: {
      type: Object,
      default: () => ({}),
    },
    sourceInfo: {
      type: Object,
      default: () => ({}),
    },
    vul: {
      type: Object,
      default: () => ({}),
    },
    images: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    localLanguage() {
      const { language } = this;
      const languageMap = {
        [LANGUAGE_TYPES.JAVA]: 'Java',
        [LANGUAGE_TYPES.JAVASCRIPT]: 'JavaScript',
      };

      return languageMap[language] || 'Java';
    },
  },
  watch: {
    sinkInfo: {
      handler(newVal) {
        this.$nextTick(() => {
          const cm = this.$refs.sinkCode.codemirror;
          const line = newVal?.sinkCodeLocation?.start_line || null;

          if (line) {
            highlightLine(cm, newVal?.sinkCode, line);
          }
        });
      },
    },
  },
  methods: {
    objectSpanMethod({ rowIndex, columnIndex }) {
      if (columnIndex === 0) {
        if (rowIndex % 4 === 0) {
          return {
            rowspan: 4,
            colspan: 1,
          };
        } else {
          return {
            rowspan: 0,
            colspan: 0,
          };
        }
      }
    },
    copyOxpeckerIp(code) {
      copyText(code);
    },
  },
  mounted() {
    this.$nextTick(() => {
      const cm = this.$refs.sinkCode.codemirror;
      const line = this.sinkInfo?.sinkCodeLocation?.start_line || null;

      if (line) {
        highlightLine(cm, this.sinkInfo?.sinkCode, line);
      }
    });
  },
};
</script>
